import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { useAdminSocket } from '../hooks/useAdminSocket';
import { SessionHeader } from '../components/SessionHeader';
import { ProjectionControlPanel } from '../components/live/ProjectionControlPanel';
import { AudienceOverview } from '../components/live/AudienceOverview';
import { VotingControlPanel } from '../components/live/VotingControlPanel';
import { QuickActionsPanel } from '../components/live/QuickActionsPanel';
import { SessionInfoCollapsible } from '../components/live/SessionInfoCollapsible';
import { ConnectedPlayersCollapsible } from '../components/live/ConnectedPlayersCollapsible';
import { BackdropsPanel } from '../components/live/BackdropsPanel';
import { PollPanel } from '../components/live/PollPanel';
import { BondsPanel } from '../components/live/BondsPanel';
import { LotteryPanel } from '../components/live/LotteryPanel';
import { VideoPanel } from '../components/live/VideoPanel';
import { ChallengePanel } from '../components/live/ChallengePanel';
import { ConsiglioPanel } from '../components/live/ConsiglioPanel';
import { BattlePanel } from '../components/live/BattlePanel';
import SoundboardPanel from '../../master/components/SoundboardPanel';

type VotingMode = 'ALL_TOGETHER' | 'SEQUENTIAL';
type TeamColor = 'BLUE' | 'RED' | 'GREEN' | 'YELLOW';

interface GameSession {
  id: string;
  name: string;
  numPlayers: 2 | 3 | 4;
  status: 'DRAFT' | 'SETUP' | 'ACTIVE' | 'COMPLETED';
  votingMode: VotingMode;
  createdAt: string;
  startedAt?: string;
  endedAt?: string;
}

const TEAM_COLORS: Record<number, TeamColor[]> = {
  2: ['RED', 'BLUE'],
  3: ['RED', 'GREEN', 'BLUE'],
  4: ['RED', 'GREEN', 'BLUE', 'YELLOW'],
};

const TEAM_COLOR_CLASSES: Record<TeamColor, { bg: string; bgActive: string; text: string; ring: string }> = {
  BLUE: { bg: 'bg-blue-600/30', bgActive: 'bg-blue-600', text: 'text-blue-300', ring: 'ring-blue-400' },
  RED: { bg: 'bg-red-600/30', bgActive: 'bg-red-600', text: 'text-red-300', ring: 'ring-red-400' },
  GREEN: { bg: 'bg-green-600/30', bgActive: 'bg-green-600', text: 'text-green-300', ring: 'ring-green-400' },
  YELLOW: { bg: 'bg-yellow-600/30', bgActive: 'bg-yellow-600', text: 'text-yellow-300', ring: 'ring-yellow-400' },
};

const TEAM_NAMES: Record<TeamColor, string> = {
  BLUE: 'Blu',
  RED: 'Rossa',
  GREEN: 'Verde',
  YELLOW: 'Gialla',
};

// Track winner info per team
interface TeamWinner {
  sheetId: string;
  sheetName: string;
}

export function SessionLive() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { socket, isConnected } = useAdminSocket(id);

  const [session, setSession] = useState<GameSession | null>(null);
  const [currentProjectionMode, setCurrentProjectionMode] = useState<string>('standby');
  const [activeVotingTeam, setActiveVotingTeam] = useState<TeamColor | null>(null);
  const [activeVotes, setActiveVotes] = useState<any[]>([]);
  const [teamWinners, setTeamWinners] = useState<Record<TeamColor, TeamWinner | null>>({
    BLUE: null,
    RED: null,
    GREEN: null,
    YELLOW: null,
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'votazioni' | 'legami' | 'streaming' | 'sfondi' | 'sondaggio' | 'lotteria' | 'video' | 'sfide' | 'consiglio' | 'battaglia'>('votazioni');
  const [sheetVoteDuration, setSheetVoteDuration] = useState(16);
  const [sheetVoteRemaining, setSheetVoteRemaining] = useState<number | null>(null);
  const [streamUrl, setStreamUrl] = useState(() => {
    // Recupera URL da localStorage o usa default
    return localStorage.getItem('streamUrl') || 'http://192.168.0.4:8080/stream';
  });
  const previousTabRef = useRef<string>('votazioni');

  // Fetch session data and projection mode on mount
  useEffect(() => {
    fetchSession();
    fetchProjectionMode();
  }, [id]);

  // Polling for votes (every 3s)
  useEffect(() => {
    fetchActiveVotes();
    const interval = setInterval(() => {
      fetchActiveVotes();
    }, 3000);
    return () => clearInterval(interval);
  }, [id, currentProjectionMode]);

  // Socket listeners for projection updates
  useEffect(() => {
    if (!socket) return;

    socket.on('admin:projection-updated', (data: any) => {
      console.log('[SessionLive] Projection updated:', data);
      setCurrentProjectionMode(data.mode.type);
      if (data.mode.activeTeam) {
        setActiveVotingTeam(data.mode.activeTeam as TeamColor);
      } else if (data.mode.type !== 'sheet-vote') {
        setActiveVotingTeam(null);
      }
      toast.success(`Modalità proiezione: ${data.mode.type}`);
    });

    // Sheet vote timer tick
    socket.on('sheet-vote:tick', (data: { voteId: string; teamColor: string; remainingSeconds: number }) => {
      setSheetVoteRemaining(data.remainingSeconds);
    });

    // Sheet vote auto-closed by server timer
    socket.on('sheet-vote:auto-closed', (data: { voteId: string; teamColor: string; sessionId: string }) => {
      console.log('[SessionLive] Sheet vote auto-closed:', data);
      setSheetVoteRemaining(null);
      fetchActiveVotes();
      setCurrentProjectionMode('sheet-vote-results');
      toast.success('Votazione scaduta - Risultati!');
    });

    return () => {
      socket.off('admin:projection-updated');
      socket.off('sheet-vote:tick');
      socket.off('sheet-vote:auto-closed');
    };
  }, [socket]);

  // Salva streamUrl in localStorage quando cambia
  useEffect(() => {
    localStorage.setItem('streamUrl', streamUrl);
  }, [streamUrl]);

  // Gestione cambio tab per streaming, sfondi, sondaggio e legami
  useEffect(() => {
    if (!socket || !isConnected) return;

    const wasStreaming = previousTabRef.current === 'streaming';
    const wasSfondi = previousTabRef.current === 'sfondi';
    const wasSondaggio = previousTabRef.current === 'sondaggio';
    const wasLegami = previousTabRef.current === 'legami';
    const wasVideo = previousTabRef.current === 'video';
    const wasSfide = previousTabRef.current === 'sfide';
    const wasConsiglio = previousTabRef.current === 'consiglio';
    const wasBattaglia = previousTabRef.current === 'battaglia';
    const isStreaming = activeTab === 'streaming';
    const isSfondi = activeTab === 'sfondi';
    const isSondaggio = activeTab === 'sondaggio';
    const isLegami = activeTab === 'legami';
    const isVideo = activeTab === 'video';
    const isSfide = activeTab === 'sfide';
    const isConsiglio = activeTab === 'consiglio';
    const isBattaglia = activeTab === 'battaglia';

    // Uscita dal tab streaming, sfondi, sondaggio, legami, video, sfide, consiglio o battaglia -> torna a standby
    if ((wasStreaming || wasSfondi || wasSondaggio || wasLegami || wasVideo || wasSfide || wasConsiglio || wasBattaglia) && !isStreaming && !isSfondi && !isSondaggio && !isLegami && !isVideo && !isSfide && !isConsiglio && !isBattaglia) {
      console.log('[SessionLive] Leaving streaming/sfondi/sondaggio/legami/video tab, setting standby');
      socket.emit('admin:set-projection', {
        sessionId: id,
        mode: { type: 'standby' },
      });
    }

    // Entrata nel tab streaming -> attiva streaming
    if (isStreaming) {
      console.log('[SessionLive] Entering streaming tab with URL:', streamUrl);
      socket.emit('admin:set-projection', {
        sessionId: id,
        mode: { type: 'streaming', streamUrl },
      });
    }

    // Entrata nel tab sfondi -> attiva sfondo nero di default
    if (isSfondi && !wasSfondi) {
      console.log('[SessionLive] Entering sfondi tab, setting black backdrop');
      socket.emit('admin:set-projection', {
        sessionId: id,
        mode: { type: 'backdrop', url: 'black' },
      });
    }

    // Entrata nel tab video -> sfondo nero di default
    if (isVideo && !wasVideo) {
      console.log('[SessionLive] Entering video tab, setting black backdrop');
      socket.emit('admin:set-projection', {
        sessionId: id,
        mode: { type: 'backdrop', url: 'black' },
      });
    }

    // Entrata nel tab legami -> standby (il BondsPanel gestisce le modalità)
    if (isLegami && !wasLegami) {
      console.log('[SessionLive] Entering legami tab, setting standby');
      socket.emit('admin:set-projection', {
        sessionId: id,
        mode: { type: 'standby' },
      });
    }

    // Entrata nel tab battaglia -> ripristina proiezione battaglia se attiva
    if (isBattaglia && !wasBattaglia) {
      fetch(`/api/battles/session/${id}/active`)
        .then((res) => res.json())
        .then((data) => {
          if (data.battle && data.battle.isActive) {
            console.log('[SessionLive] Entering battaglia tab, restoring battle projection');
            socket.emit('admin:set-projection', {
              sessionId: id,
              mode: { type: 'battle', sessionId: id, battleId: data.battle.id, battleState: data.battle },
            });
          }
        })
        .catch((err) => console.error('[SessionLive] Error restoring battle projection:', err));
    }

    previousTabRef.current = activeTab;
  }, [activeTab, socket, isConnected, id, streamUrl]);

  const fetchSession = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/sessions/${id}`);
      const data = await response.json();
      setSession(data.session || data);
    } catch (error) {
      console.error('[SessionLive] Error fetching session:', error);
      toast.error('Errore caricamento sessione');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectionMode = async () => {
    try {
      const response = await fetch(`/api/sessions/${id}/projection-mode`);
      if (response.ok) {
        const data = await response.json();
        if (data.mode?.type) {
          setCurrentProjectionMode(data.mode.type);
          if (data.mode.activeTeam) {
            setActiveVotingTeam(data.mode.activeTeam as TeamColor);
          } else {
            setActiveVotingTeam(null);
          }
          console.log('[SessionLive] Loaded projection mode:', data.mode.type, 'activeTeam:', data.mode.activeTeam);
        }
      }
    } catch (error) {
      console.error('[SessionLive] Error fetching projection mode:', error);
    }
  };

  const fetchActiveVotes = async () => {
    try {
      // Always fetch ALL votes to track winners
      const response = await fetch(`/api/sheet-votes/session/${id}/all`);
      if (response.ok) {
        const data = await response.json();
        const votes = data.votes || [];
        setActiveVotes(votes);

        // Extract winners from closed votes
        const winners: Record<TeamColor, TeamWinner | null> = {
          BLUE: null, RED: null, GREEN: null, YELLOW: null,
        };

        for (const vote of votes) {
          if (!vote.isActive && vote.finalSheetId && vote.finalSheet) {
            const team = vote.teamColor as TeamColor;
            // Keep winner (we only have one vote per team at a time)
            if (!winners[team]) {
              winners[team] = {
                sheetId: vote.finalSheetId,
                sheetName: vote.finalSheet.name,
              };
            }
          }
        }
        setTeamWinners(winners);
      }
    } catch (error) {
      console.error('[SessionLive] Error fetching votes:', error);
    }
  };

  const handleBackToSetup = () => {
    const hasActiveVotes = activeVotes.some((v: any) => v.isActive);
    if (hasActiveVotes) {
      if (!confirm('Ci sono votazioni attive. Tornare al Setup?')) return;
    }
    navigate(`/admin/session/${id}`);
  };

  const handleSetProjectionMode = async (modeType: string) => {
    if (!socket || !isConnected) {
      toast.error('Connessione Socket.IO persa. Aggiorna la pagina.');
      return;
    }

    const isSequentialMode = session?.votingMode === 'SEQUENTIAL';

    try {
      // If entering sheet-vote mode, start voting automatically
      if (modeType === 'sheet-vote' && currentProjectionMode !== 'sheet-vote') {
        if (isSequentialMode) {
          // SEQUENTIAL: must have a team selected
          if (!activeVotingTeam) {
            toast.error('Seleziona prima una squadra');
            return;
          }

          // Delete any previous votes for this team (allows re-voting)
          const existingVotes = activeVotes.filter(
            (v: any) => v.teamColor === activeVotingTeam
          );
          for (const vote of existingVotes) {
            console.log(`[SessionLive] Deleting previous vote ${vote.id} for team ${activeVotingTeam}`);
            await fetch(`/api/sheet-votes/${vote.id}`, { method: 'DELETE' });
          }

          // Clear the winner for this team
          setTeamWinners((prev) => ({ ...prev, [activeVotingTeam]: null }));

          // Start new voting for this team
          console.log(`[SessionLive] Starting voting for team ${activeVotingTeam}...`);
          const response = await fetch('/api/sheet-votes/team', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId: id, teamColor: activeVotingTeam, durationSeconds: sheetVoteDuration }),
          });
          if (response.ok) {
            await fetchActiveVotes();
            setSheetVoteRemaining(sheetVoteDuration);
            toast.success(`Votazione squadra ${activeVotingTeam} avviata!`);
          } else {
            const error = await response.json();
            toast.error(error.error || 'Errore avvio votazione');
            return;
          }
        } else {
          // ALL_TOGETHER: start voting for all teams
          const hasActiveVotes = activeVotes.some((v: any) => v.isActive);
          if (!hasActiveVotes) {
            console.log('[SessionLive] Auto-starting voting for all teams...');
            const response = await fetch(`/api/sheet-votes/session/${id}/start`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ durationSeconds: sheetVoteDuration }),
            });
            if (response.ok) {
              await fetchActiveVotes();
              setSheetVoteRemaining(sheetVoteDuration);
              toast.success('Votazione avviata!');
            }
          }
        }
      }

      // If entering sheet-vote-results or winners, close votes first
      if (modeType === 'sheet-vote-results' || modeType === 'sheet-vote-winners') {
        setSheetVoteRemaining(null);
        if (isSequentialMode && activeVotingTeam) {
          // SEQUENTIAL: close only the active team's vote
          const teamVote = activeVotes.find(
            (v: any) => v.isActive && v.teamColor === activeVotingTeam
          );
          if (teamVote) {
            console.log(`[SessionLive] Closing voting for team ${activeVotingTeam}...`);
            await fetch(`/api/sheet-votes/${teamVote.id}/close`, {
              method: 'POST',
            });
            await fetchActiveVotes();
            toast.success(`Votazione squadra ${activeVotingTeam} chiusa!`);
          }
        } else {
          // ALL_TOGETHER: close all votes
          const hasActiveVotes = activeVotes.some((v: any) => v.isActive);
          if (hasActiveVotes) {
            console.log('[SessionLive] Auto-closing voting for results...');
            await fetch(`/api/sheet-votes/session/${id}/close-all`, {
              method: 'POST',
            });
            await fetchActiveVotes();
            toast.success('Votazioni chiuse!');
          }
        }
      }

      // If exiting sheet-vote mode to something else (not results/winners), close votes
      if (currentProjectionMode === 'sheet-vote' && modeType !== 'sheet-vote' && modeType !== 'sheet-vote-results' && modeType !== 'sheet-vote-winners') {
        if (isSequentialMode && activeVotingTeam) {
          // SEQUENTIAL: close only the active team's vote
          const teamVote = activeVotes.find(
            (v: any) => v.isActive && v.teamColor === activeVotingTeam
          );
          if (teamVote) {
            await fetch(`/api/sheet-votes/${teamVote.id}/close`, {
              method: 'POST',
            });
            await fetchActiveVotes();
            toast.success(`Votazione squadra ${activeVotingTeam} chiusa!`);
          }
        } else {
          // ALL_TOGETHER: close all votes
          console.log('[SessionLive] Auto-closing voting...');
          await fetch(`/api/sheet-votes/session/${id}/close-all`, {
            method: 'POST',
          });
          await fetchActiveVotes();
          toast.success('Votazioni chiuse!');
        }
      }

      // Construct mode
      let mode: any = { type: modeType };

      if (modeType === 'sheet-vote' || modeType === 'sheet-vote-results' || modeType === 'sheet-vote-winners') {
        mode.sessionId = id;
        // In SEQUENTIAL mode, pass activeTeam to filter display
        if (isSequentialMode && activeVotingTeam) {
          mode.activeTeam = activeVotingTeam;
        }
      }

      // Emit to server
      socket.emit('admin:set-projection', {
        sessionId: id,
        mode,
      });

      // State will be updated via 'admin:projection-updated' listener
      console.log(`[SessionLive] Projection mode change requested: ${modeType}`, mode);
    } catch (error) {
      console.error('[SessionLive] Error setting projection mode:', error);
      toast.error('Errore impostazione proiezione');
    }
  };

  const handleSetActiveTeam = (teamColor: TeamColor) => {
    // Check if there's a LIVE vote for ANY team
    const hasLiveVote = activeVotes.some((v: any) => v.isActive);

    if (hasLiveVote && currentProjectionMode === 'sheet-vote') {
      toast.error('Chiudi prima la votazione corrente (vai su Results o Standby)');
      return;
    }

    setActiveVotingTeam(teamColor);

    const winner = teamWinners[teamColor];
    if (winner) {
      toast.success(`Squadra ${teamColor} selezionata (già votato: ${winner.sheetName})`);
    } else {
      toast.success(`Squadra ${teamColor} selezionata`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 p-4">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-12 bg-gray-800 rounded"></div>
            <div className="h-64 bg-gray-800 rounded"></div>
            <div className="h-64 bg-gray-800 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex overflow-hidden bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#1f2937',
            color: '#fff',
            border: '1px solid #374151',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      {/* Main content */}
      <div className="flex-1 min-w-0 overflow-y-auto p-4">
      <div className="max-w-7xl mx-auto space-y-6">
        <SessionHeader mode="live" session={session} onNavigate={handleBackToSetup} />

        {/* Tab Navigation */}
        <div className="flex gap-2 border-b border-gray-700 pb-2">
          {[
            { id: 'votazioni', label: '🗳️ Scelta Eroi', icon: '🗳️' },
            { id: 'legami', label: '🔗 Legami', icon: '🔗' },
            { id: 'video', label: '🎬 Video', icon: '🎬' },
            { id: 'streaming', label: '📡 Streaming', icon: '📡' },
            { id: 'sfondi', label: '🖼️ Sfondi', icon: '🖼️' },
            { id: 'sondaggio', label: '📊 Sondaggio', icon: '📊' },
            { id: 'lotteria', label: '🎲 Lotteria', icon: '🎲' },
            { id: 'sfide', label: '🏆 Sfide', icon: '🏆' },
            { id: 'consiglio', label: '💬 Consiglio', icon: '💬' },
            { id: 'battaglia', label: '⚔️ Battaglia', icon: '⚔️' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`px-6 py-3 rounded-t-lg font-semibold transition-all ${
                activeTab === tab.id
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'votazioni' && (
          <div className="space-y-4">
            {/* Team Selection for Sequential Voting Mode - Compact */}
            {session?.votingMode === 'SEQUENTIAL' && (
              <div className="bg-gray-800 rounded-lg p-4 border border-yellow-600/50">
                <div className="flex items-center gap-4 flex-wrap">
                  <span className="text-gray-400 text-sm font-semibold whitespace-nowrap">Squadra:</span>
                  {session && TEAM_COLORS[session.numPlayers].map((color) => {
                    const classes = TEAM_COLOR_CLASSES[color];
                    const isSelected = activeVotingTeam === color;
                    const teamVote = activeVotes.find((v: any) => v.teamColor === color && v.isActive);
                    const isLive = !!teamVote;
                    const winner = teamWinners[color];
                    const hasLiveVote = activeVotes.some((v: any) => v.isActive);
                    const isDisabled = hasLiveVote && currentProjectionMode === 'sheet-vote' && !isLive;

                    return (
                      <button
                        key={color}
                        onClick={() => handleSetActiveTeam(color)}
                        disabled={isDisabled}
                        className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                          isSelected
                            ? `${classes.bgActive} text-white ring-2 ${classes.ring}`
                            : winner
                            ? `${classes.bg} ${classes.text} ring-2 ring-green-500`
                            : `${classes.bg} ${classes.text} hover:opacity-80`
                        } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <span>{TEAM_NAMES[color]}</span>
                        {isLive && (
                          <span className="bg-yellow-500 text-black text-xs px-1.5 py-0.5 rounded-full font-bold animate-pulse">
                            LIVE
                          </span>
                        )}
                        {winner && !isLive && (
                          <span className="text-green-400 text-sm">✓</span>
                        )}
                      </button>
                    );
                  })}

                  {/* Status message inline */}
                  <span className="text-sm ml-auto">
                    {activeVotingTeam && currentProjectionMode === 'sheet-vote' && (
                      <span className="text-yellow-400">⏳ Votazione {TEAM_NAMES[activeVotingTeam as TeamColor]} in corso</span>
                    )}
                    {activeVotingTeam && currentProjectionMode !== 'sheet-vote' && !teamWinners[activeVotingTeam] && (
                      <span className="text-green-400">Pronta → Votazione</span>
                    )}
                    {activeVotingTeam && currentProjectionMode !== 'sheet-vote' && teamWinners[activeVotingTeam] && (
                      <span className="text-yellow-400">⚠️ Ri-voto</span>
                    )}
                    {!activeVotingTeam && (
                      <span className="text-gray-500">← Seleziona squadra</span>
                    )}
                  </span>
                </div>
              </div>
            )}

            {/* Projection modes + Audience count inline */}
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <ProjectionControlPanel
                  sessionId={id!}
                  currentMode={currentProjectionMode}
                  isConnected={isConnected}
                  onSetMode={handleSetProjectionMode}
                  compact={true}
                />
                <AudienceOverview sessionId={id!} refreshInterval={3000} compact={true} />
              </div>
            </div>

            {/* Timer config */}
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="flex items-center gap-4">
                <label className="text-gray-400 text-sm font-semibold whitespace-nowrap">Durata votazione:</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={sheetVoteDuration}
                  onChange={(e) => setSheetVoteDuration(e.target.value as any)}
                  onBlur={(e) => { const n = parseInt(e.target.value); setSheetVoteDuration(isNaN(n) ? 16 : Math.max(5, Math.min(120, n))); }}
                  className="w-20 bg-gray-700 text-white rounded px-2 py-1 text-center"
                />
                <span className="text-gray-500 text-sm">secondi</span>
                {sheetVoteRemaining !== null && sheetVoteRemaining > 0 && (
                  <span className="ml-auto text-yellow-400 font-bold text-lg animate-pulse">
                    ⏱️ {sheetVoteRemaining}s
                  </span>
                )}
              </div>
            </div>

            {/* Voting Control - Full width */}
            <VotingControlPanel
              sessionId={id!}
              activeVotes={activeVotes}
              onRefresh={fetchActiveVotes}
              projectionMode={currentProjectionMode}
              activeVotingTeam={activeVotingTeam}
              votingMode={session?.votingMode}
              teamWinners={teamWinners}
              numPlayers={session?.numPlayers}
            />
          </div>
        )}

        {activeTab === 'legami' && (
          <BondsPanel
            sessionId={id!}
            socket={socket}
            isConnected={isConnected}
            numPlayers={session?.numPlayers || 2}
          />
        )}

        {activeTab === 'streaming' && (
          <div className="space-y-4">
            {/* URL Input */}
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="flex items-center gap-4">
                <label className="text-gray-400 text-sm font-semibold whitespace-nowrap">
                  URL Stream MJPEG:
                </label>
                <input
                  type="text"
                  value={streamUrl}
                  onChange={(e) => setStreamUrl(e.target.value)}
                  className="flex-1 bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="http://192.168.0.4:8080/stream"
                />
                <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}
                     title={isConnected ? 'Connesso' : 'Disconnesso'} />
              </div>
            </div>

            {/* Stream Status (no preview to avoid double connection) */}
            <div className="bg-gray-900 rounded-lg border border-gray-700 p-8">
              <div className="text-center">
                <div className="text-6xl mb-4">📡</div>
                <p className="text-xl text-white font-semibold mb-2">Streaming Attivo</p>
                <p className="text-gray-400 text-sm mb-4">
                  Lo stream viene mostrato sulla finestra Display
                </p>
                <div className="bg-gray-800 rounded-lg p-3 inline-block">
                  <code className="text-purple-400 text-sm break-all">{streamUrl}</code>
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <p className="text-gray-400 text-sm">
                <span className="text-purple-400 font-semibold">Display:</span> Mostra lo stream MJPEG a schermo intero con bande nere
              </p>
              <p className="text-gray-400 text-sm mt-1">
                <span className="text-blue-400 font-semibold">Audience:</span> Vede la schermata di Standby
              </p>
            </div>
          </div>
        )}

        {activeTab === 'sfondi' && (
          <BackdropsPanel
            sessionId={id!}
            socket={socket}
            isConnected={isConnected}
          />
        )}

        {activeTab === 'sondaggio' && (
          <PollPanel
            sessionId={id!}
            socket={socket}
            isConnected={isConnected}
            numPlayers={session?.numPlayers || 2}
          />
        )}

        {activeTab === 'lotteria' && (
          <LotteryPanel
            sessionId={id!}
            socket={socket}
            isConnected={isConnected}
          />
        )}

        {activeTab === 'video' && (
          <VideoPanel
            sessionId={id!}
            socket={socket}
            isConnected={isConnected}
          />
        )}

        {activeTab === 'sfide' && (
          <ChallengePanel
            sessionId={id!}
            socket={socket}
            isConnected={isConnected}
          />
        )}

        {activeTab === 'consiglio' && (
          <ConsiglioPanel
            sessionId={id!}
            socket={socket}
            isConnected={isConnected}
          />
        )}

        {activeTab === 'battaglia' && (
          <BattlePanel
            sessionId={id!}
            socket={socket}
            isConnected={isConnected}
          />
        )}

        {/* Quick Actions - Only in Scelta Eroi tab */}
        {activeTab === 'votazioni' && (
          <QuickActionsPanel
            sessionId={id!}
            socket={socket}
            isConnected={isConnected}
          />
        )}

        {/* Collapsible Players - Always visible */}
        {session && (
          <ConnectedPlayersCollapsible
            sessionId={id!}
            numPlayers={session.numPlayers}
            refreshInterval={5000}
          />
        )}

        {/* Collapsible Info - Always visible */}
        <SessionInfoCollapsible
          session={session}
          activeVotes={activeVotes}
          socketConnected={isConnected}
        />
      </div>
      </div>
      {/* Soundboard sidebar */}
      <div className="w-[280px] flex-shrink-0 border-l border-gray-700">
        <SoundboardPanel />
      </div>
    </div>
  );
}
